import homeFill from '@iconify/icons-eva/home-fill';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import { Icon } from '@iconify/react';
import { Box, Button, Divider, MenuItem, Typography } from '@mui/material';
// material
import { dispatch, RootState, useSelector } from '@/redux/store';
import { alpha } from '@mui/material/styles';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
// components
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { client } from '@/index';
import { logoutMutation } from '@/redux/slices/auth';
import { getErrorMessage } from '@/utils/stringHelper';
import { MIconButton } from '../../components/@material-extend';
import MenuPopover from '../../components/MenuPopover';
import MyAvatar from '../../components/MyAvatar';
import { PATH_DASHBOARD } from '../../routes/paths';

// ----------------------------------------------------------------------

const MENU_OPTIONS = () => {
  const { t } = useTranslation();
  return [
    {
      label: t('home'),
      icon: homeFill,
      linkTo: '/'
    },
    {
      label: t('Settings'),
      icon: settings2Fill,
      linkTo: PATH_DASHBOARD.user.account
    }
  ];
};

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const anchorRef = useRef(null);
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const { showSnackbar } = useSnackbarHelper();
  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogoutBtnOnClick = async () => {
    try {
      handleClose();
      await logout();
    } catch (error) {
      console.error(getErrorMessage(error));
      showSnackbar('Logout failed', 'error');
    }
  };

  const logout = async () => {
    const logoutResult = await dispatch(logoutMutation({})).unwrap();
    if (logoutResult) {
      localStorage.clear();
      await client.clearStore();
      window.location.reload();
      return;
    }
    throw new Error('Logout failed');
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72)
            }
          })
        }}
      >
        <MyAvatar />
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {user?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {MENU_OPTIONS()?.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: 'body2', py: 1, px: 2.5 }}
          >
            <Box
              component={Icon}
              icon={option.icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24
              }}
            />

            {option.label}
          </MenuItem>
        ))}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogoutBtnOnClick}>
            {t('Logout')}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
